import { asyncThunkWrapper } from "../handleAsyncThunk"
import { BASEURI, headers } from "../../utils/helper"
import { createAsyncThunk } from "@reduxjs/toolkit"

// ─── GET ALL SPECIALIST WALLETS (table view) ───────────────────────────────
export const fetchAllSpecialistWallets = createAsyncThunk(
  "wallet/fetchAllSpecialists",
  async (data, thunkAPI) => {
    const params = {
      page: data?.page || 1,
      limit: data?.limit || 20,
      search: data?.search || undefined,
      onlyWithBalance: data?.onlyWithBalance || undefined,
      onlyFlagged: data?.onlyFlagged || undefined,
    }
    return asyncThunkWrapper(
      { method: "GET", url: `${BASEURI}/admin/wallet/specialists`, params, headers: headers() },
      thunkAPI,
      { showToast: false, returnDataKey: "data" }
    )
  }
)

// ─── GET SINGLE SPECIALIST WALLET ─────────────────────────────────────────
export const fetchSpecialistWallet = createAsyncThunk(
  "wallet/fetchOne",
  async ({ specialistId }, thunkAPI) => {
    return asyncThunkWrapper(
      { method: "GET", url: `${BASEURI}/admin/wallet/${specialistId}`, headers: headers() },
      thunkAPI,
      { showToast: false, returnDataKey: "data" }
    )
  }
)

// ─── GET SPECIALIST WALLET TRANSACTIONS ───────────────────────────────────
export const fetchSpecialistTransactions = createAsyncThunk(
  "wallet/fetchTransactions",
  async ({ specialistId, page = 1, limit = 20, type = null }, thunkAPI) => {
    const params = { page, limit, ...(type ? { type } : {}) }
    return asyncThunkWrapper(
      { method: "GET", url: `${BASEURI}/admin/wallet/${specialistId}/transactions`, params, headers: headers() },
      thunkAPI,
      { showToast: false, returnDataKey: "data" }
    )
  }
)

// ─── TRIGGER PAYOUT ────────────────────────────────────────────────────────
export const triggerPayout = createAsyncThunk(
  "wallet/triggerPayout",
  async ({ specialistId, amount, currency, note, idempotencyKey }, thunkAPI) => {
    return asyncThunkWrapper(
      {
        method: "POST",
        url: `${BASEURI}/admin/wallet/${specialistId}/payout`,
        data: { amount, currency, note, idempotencyKey },
        headers: headers(),
      },
      thunkAPI,
      { showToast: true, successMsg: "Payout initiated successfully!", returnDataKey: "data" }
    )
  }
)

// ─── RETRY FAILED PAYOUT ───────────────────────────────────────────────────
export const retryPayout = createAsyncThunk(
  "wallet/retryPayout",
  async ({ payoutId }, thunkAPI) => {
    return asyncThunkWrapper(
      { method: "POST", url: `${BASEURI}/admin/wallet/payout/${payoutId}/retry`, headers: headers() },
      thunkAPI,
      { showToast: true, successMsg: "Payout retry initiated!", returnDataKey: "data" }
    )
  }
)

// ─── GET ALL PAYOUTS ───────────────────────────────────────────────────────
export const fetchAllPayouts = createAsyncThunk(
  "wallet/fetchAllPayouts",
  async (data, thunkAPI) => {
    const params = {
      page: data?.page || 1,
      limit: data?.limit || 20,
      specialistId: data?.specialistId || undefined,
      status: data?.status || undefined,
      startDate: data?.startDate || undefined,
      endDate: data?.endDate || undefined,
    }
    return asyncThunkWrapper(
      { method: "GET", url: `${BASEURI}/admin/wallet/payouts`, params, headers: headers() },
      thunkAPI,
      { showToast: false, returnDataKey: "data" }
    )
  }
)

// ─── GET FLAGGED WALLETS ───────────────────────────────────────────────────
export const fetchFlaggedWallets = createAsyncThunk(
  "wallet/fetchFlagged",
  async ({ page = 1, limit = 20 } = {}, thunkAPI) => {
    return asyncThunkWrapper(
      { method: "GET", url: `${BASEURI}/admin/wallet/flagged`, params: { page, limit }, headers: headers() },
      thunkAPI,
      { showToast: false, returnDataKey: "data" }
    )
  }
)

// ─── GET COMMISSION SUMMARY ────────────────────────────────────────────────
export const fetchCommissionSummary = createAsyncThunk(
  "wallet/fetchCommissionSummary",
  async ({ startDate, endDate } = {}, thunkAPI) => {
    const params = {}
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate
    return asyncThunkWrapper(
      { method: "GET", url: `${BASEURI}/admin/wallet/commission-summary`, params, headers: headers() },
      thunkAPI,
      { showToast: false, returnDataKey: "data" }
    )
  }
)

// ─── PAUSE PAYOUTS ─────────────────────────────────────────────────────────
export const pausePayouts = createAsyncThunk(
  "wallet/pause",
  async ({ specialistId, reason }, thunkAPI) => {
    return asyncThunkWrapper(
      { method: "PATCH", url: `${BASEURI}/admin/wallet/${specialistId}/pause`, data: { reason }, headers: headers() },
      thunkAPI,
      { showToast: true, successMsg: "Payouts paused successfully.", returnDataKey: "data" }
    )
  }
)

// ─── RESUME PAYOUTS ────────────────────────────────────────────────────────
export const resumePayouts = createAsyncThunk(
  "wallet/resume",
  async ({ specialistId }, thunkAPI) => {
    return asyncThunkWrapper(
      { method: "PATCH", url: `${BASEURI}/admin/wallet/${specialistId}/resume`, headers: headers() },
      thunkAPI,
      { showToast: true, successMsg: "Payouts resumed.", returnDataKey: "data" }
    )
  }
)

// ─── FLAG FOR REVIEW ───────────────────────────────────────────────────────
export const flagForReview = createAsyncThunk(
  "wallet/flag",
  async ({ specialistId, reason }, thunkAPI) => {
    return asyncThunkWrapper(
      { method: "PATCH", url: `${BASEURI}/admin/wallet/${specialistId}/flag`, data: { reason }, headers: headers() },
      thunkAPI,
      { showToast: true, successMsg: "Specialist flagged for review. Payouts paused.", returnDataKey: "data" }
    )
  }
)

// ─── UNFLAG ACCOUNT ────────────────────────────────────────────────────────
export const unflagAccount = createAsyncThunk(
  "wallet/unflag",
  async ({ specialistId }, thunkAPI) => {
    return asyncThunkWrapper(
      { method: "PATCH", url: `${BASEURI}/admin/wallet/${specialistId}/unflag`, headers: headers() },
      thunkAPI,
      { showToast: true, successMsg: "Flag removed. Remember to resume payouts manually.", returnDataKey: "data" }
    )
  }
)
